import { useEffect, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import { startLogout } from "../../actions/auth";
import { SimpleAlert } from "./Alerts";
import { ALERT_ICON_TYPE_INFO } from "../../utils/constants";
import {
  BUTTON_CLOSE,
  SUPPORT_SESSION_BANNER_DESCRIPTION,
  SUPPORT_SESSION_BANNER_TITLE,
  SUPPORT_SESSION_ENDED_DESCRIPTION,
  SUPPORT_SESSION_ENDED_TITLE,
} from "../../i18n/keysTranslations";
import {
  isSupportSessionExpired,
  SUPPORT_SESSION_CHECK_INTERVAL_MS,
} from "../../utils/supportSession";

/**
 * Shown while an owner "login as client" support session is active. Signs the
 * session out when its time window ends (the backend also rejects it then).
 */
const SupportSessionBanner = () => {
  const expiresAt = useSelectorApp(
    (state) => state.auth.user?.supportSessionExpiresAt ?? null
  );
  const dispatch = useDispatchApp();
  const { t, i18n } = useTranslationApp();
  const hasEndedRef = useRef(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (expiresAt === null) {
      return undefined;
    }

    hasEndedRef.current = false;
    setDismissed(false);

    const endIfExpired = async () => {
      if (hasEndedRef.current || !isSupportSessionExpired(expiresAt)) {
        return;
      }

      hasEndedRef.current = true;
      await dispatch(startLogout());
      SimpleAlert({
        title: t(SUPPORT_SESSION_ENDED_TITLE),
        text: t(SUPPORT_SESSION_ENDED_DESCRIPTION),
        icon: ALERT_ICON_TYPE_INFO,
      });
    };

    endIfExpired();
    const intervalID = setInterval(
      endIfExpired,
      SUPPORT_SESSION_CHECK_INTERVAL_MS
    );
    document.addEventListener("visibilitychange", endIfExpired);

    return () => {
      clearInterval(intervalID);
      document.removeEventListener("visibilitychange", endIfExpired);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt]);

  if (expiresAt === null || dismissed) {
    return null;
  }

  const endTime = new Date(expiresAt).toLocaleTimeString(i18n.language, {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1300,
        width: "90%",
        maxWidth: 600,
      }}
    >
      <Alert
        severity="info"
        role="status"
        data-testid="support-session-banner"
        onClose={() => setDismissed(true)}
        action={
          <IconButton
            size="small"
            aria-label={t(BUTTON_CLOSE)}
            data-testid="support-session-banner-close"
            onClick={() => setDismissed(true)}
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        sx={{
          backgroundColor: "#fff",
          color: "text.primary",
          borderRadius: 2,
          boxShadow: "0 2px 16px rgba(0,0,0,0.15)",
          "& .MuiAlert-icon": { color: "info.main" },
        }}
      >
        <AlertTitle sx={{ fontWeight: 700 }}>
          {t(SUPPORT_SESSION_BANNER_TITLE)}
        </AlertTitle>
        {t(SUPPORT_SESSION_BANNER_DESCRIPTION, { time: endTime })}
      </Alert>
    </Box>
  );
};

export default SupportSessionBanner;
